import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Code, History, Info, Layout, Loader2, Monitor, Search, Sliders, Smartphone, User, X } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import AgentPageShell from '../../components/agent/AgentPageShell';
import CodeSnippet from '../../components/agent/CodeSnippet';
import { ResolutionBadge } from '../../components/agent/StatusBadge';
import { cn } from '../../lib/utils';
import { fetchChannels, fetchSessions } from '../../lib/agentApi';
import { formatWhen } from './AgentDashboardPage';

const ChatHistoryPage = () => (
  <AgentPageShell
    title="Chat History Drawer"
    description="The in-app panel where end users review their own past conversations"
    icon={History}
    breadcrumb={['Chat History Drawer']}
  >
    {({ agent, orgId }) => <HistoryBody key={agent.id} agent={agent} orgId={orgId} />}
  </AgentPageShell>
);

const HistoryBody = ({ agent, orgId }) => {
  const [sessions, setSessions] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [position, setPosition] = useState('right');
  const [settings, setSettings] = useState({
    backdrop_blur: true,
    show_badges: true,
    auto_load: true,
    enable_search: true,
    max_items: 20,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sessionResult, channelResult] = await Promise.all([
        fetchSessions(orgId, agent.id, { page: 1, page_size: 25 }),
        fetchChannels(orgId, agent.id),
      ]);
      setSessions(sessionResult.sessions || []);
      setChannels(channelResult.channels || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load history');
    } finally {
      setLoading(false);
    }
  }, [orgId, agent.id]);

  useEffect(() => {
    load();
  }, [load]);

  const key = channels[0]?.publishable_key || 'dt_pk_YOUR_KEY';

  // Roll sessions up per end user — this is the drawer's own analytics view.
  const byUser = sessions.reduce((acc, session) => {
    const id = session.end_user_id || 'anonymous';
    const entry = acc[id] || { id, sessions: 0, last: session.created_at, states: {} };
    entry.sessions += 1;
    if ((session.created_at || '') > (entry.last || '')) entry.last = session.created_at;
    entry.states[session.resolution_state] = (entry.states[session.resolution_state] || 0) + 1;
    acc[id] = entry;
    return acc;
  }, {});
  const users = Object.values(byUser)
    .map((u) => ({
      ...u,
      top: Object.entries(u.states).sort((a, b) => b[1] - a[1])[0]?.[0] || 'pending',
    }))
    .sort((a, b) => b.sessions - a.sessions)
    .slice(0, 10);

  const previewSessions = sessions.slice(0, settings.max_items);

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-8">
          {/* Mockups */}
          <div className="bg-muted/30 rounded-3xl p-8 border border-border/50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 justify-items-center">
              <DrawerMockup
                mode="light"
                direction="ltr"
                title="Chat History"
                label={
                  <>
                    <Monitor className="w-3.5 h-3.5 text-brand-blue" />
                    Light — English (LTR)
                  </>
                }
                sessions={previewSessions}
                settings={settings}
                position={position}
              />
              <DrawerMockup
                mode="dark"
                direction="rtl"
                title="سجل المحادثات"
                label={
                  <>
                    <Smartphone className="w-3.5 h-3.5 text-brand-blue-light" />
                    Dark — Arabic (RTL)
                  </>
                }
                sessions={previewSessions}
                settings={settings}
                position={position}
              />
            </div>
            <p className="text-center text-xs text-muted-foreground mt-6">
              Hover a device to slide the drawer in. RTL opens from the opposite edge.
            </p>
          </div>

          {/* Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-5">
              <div className="flex items-center gap-2 border-b border-border pb-3">
                <Layout className="w-5 h-5 text-brand-blue" />
                <h2 className="font-semibold">Drawer Settings</h2>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-sm">Drawer position</Label>
                  <p className="text-xs text-muted-foreground">Slide-in edge</p>
                </div>
                <div className="flex p-1 bg-muted rounded-lg border border-border">
                  {['left', 'right', 'bottom'].map((pos) => (
                    <button
                      key={pos}
                      onClick={() => setPosition(pos)}
                      className={cn(
                        'px-3 py-1 text-xs rounded-md capitalize transition-all',
                        position === pos
                          ? 'bg-background shadow-sm font-semibold'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {pos}
                    </button>
                  ))}
                </div>
              </div>

              <SettingToggle
                label="Backdrop blur"
                hint="Blur the app behind the drawer"
                checked={settings.backdrop_blur}
                onChange={(v) => setSettings((s) => ({ ...s, backdrop_blur: v }))}
              />
              <SettingToggle
                label="Show status badges"
                hint="Display the resolution state per conversation"
                checked={settings.show_badges}
                onChange={(v) => setSettings((s) => ({ ...s, show_badges: v }))}
              />
              <SettingToggle
                label="Auto-load newest"
                hint="Refresh history in real time"
                checked={settings.auto_load}
                onChange={(v) => setSettings((s) => ({ ...s, auto_load: v }))}
              />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-sm">Max history items</Label>
                  <p className="text-xs text-muted-foreground">Loaded per page</p>
                </div>
                <Input
                  type="number"
                  min={5}
                  max={100}
                  className="w-20 h-8 text-center text-xs"
                  value={settings.max_items}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, max_items: Number(e.target.value) || 5 }))
                  }
                />
              </div>
            </div>

            <div className="space-y-5">
              <div className="flex items-center gap-2 border-b border-border pb-3">
                <Sliders className="w-5 h-5 text-brand-blue" />
                <h2 className="font-semibold">Search & Filter</h2>
              </div>

              <SettingToggle
                label="Enable search"
                hint="Let users search their own history"
                checked={settings.enable_search}
                onChange={(v) => setSettings((s) => ({ ...s, enable_search: v }))}
              />

              <div className="space-y-2">
                <Label className="text-sm">Filter chips</Label>
                <div className="flex flex-wrap gap-2">
                  {['Resolved', 'Escalated', 'Voice', 'Text', 'Date'].map((chip) => (
                    <span
                      key={chip}
                      className="px-2 py-1 bg-muted border border-border rounded text-[10px] font-medium text-muted-foreground"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              </div>

              <Card className="bg-muted/20">
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-brand-blue" />
                    <h3 className="text-sm font-semibold">Privacy scope</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    The drawer only ever returns conversations belonging to the end user the host
                    app identifies. How that identity is verified is still an open decision —
                    until it is settled, treat history as readable by anyone holding the key.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="lg:col-span-4 space-y-6">
          <div className="space-y-3">
            <h2 className="font-semibold flex items-center gap-2">
              <Code className="w-4 h-4 text-brand-blue" />
              SDK Integration
            </h2>
            <CodeSnippet
              snippets={{
                js: {
                  label: 'JavaScript',
                  code: `DocTreeAgent.showHistory({
  agentKey: "${key}",
  userId: "user_123",
  maxItems: ${settings.max_items},
  position: "${position}",
  theme: "auto",
  onOpen: () => console.log("history opened"),
});`,
                },
                ionic: {
                  label: 'Ionic',
                  code: `import { DocTreeAgent } from '@doctree/agent-web';

openHistory() {
  DocTreeAgent.showHistory({
    agentKey: '${key}',
    userId: this.auth.userId,
    position: '${position}',
  });
}`,
                },
              }}
            />
            <p className="text-xs text-muted-foreground leading-relaxed">
              Call this from any button in your app to open the drawer.
            </p>
          </div>

          <Card>
            <CardContent className="p-5 space-y-3">
              <h3 className="text-sm font-semibold">This agent's history</h3>
              <Row label="Conversations" value={sessions.length} />
              <Row label="Distinct users" value={Object.keys(byUser).length} />
              <Row
                label="Deployment keys"
                value={channels.length || 'none yet'}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Analytics */}
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-bold">Recent History Accesses</h2>
          <p className="text-sm text-muted-foreground">
            Conversation volume per end user for this agent
          </p>
        </div>
        <Card className="overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading…
            </div>
          ) : users.length === 0 ? (
            <div className="py-16 text-center">
              <User className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                No end-user conversations yet.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader className="bg-muted/40">
                <TableRow>
                  <TableHead>End user</TableHead>
                  <TableHead className="text-center">Conversations</TableHead>
                  <TableHead>Last activity</TableHead>
                  <TableHead>Most frequent status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-mono text-xs">{maskUser(user.id)}</TableCell>
                    <TableCell className="text-center font-medium">{user.sessions}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatWhen(user.last)}
                    </TableCell>
                    <TableCell>
                      <ResolutionBadge state={user.top} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
    </div>
  );
};

const DrawerMockup = ({ mode, direction, title, label, sessions, settings, position }) => {
  const isDark = mode === 'dark';
  const isRtl = direction === 'rtl';
  const fromLeft = position === 'left' ? !isRtl : isRtl;

  return (
    <div className="space-y-3 flex flex-col items-center">
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-1 rounded-full border shadow-sm text-xs font-medium',
          isDark ? 'bg-slate-900 border-slate-700 text-white' : 'bg-card border-border'
        )}
      >
        {label}
      </div>

      <div
        className={cn(
          'relative w-[270px] h-[480px] rounded-[2.5rem] border-[8px] overflow-hidden shadow-2xl group',
          isDark ? 'border-slate-700 bg-slate-900' : 'border-slate-800 bg-slate-50'
        )}
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-slate-800 rounded-b-2xl z-50" />

        <div className={cn('w-full h-full p-4 pt-9', isDark ? 'bg-slate-900' : 'bg-slate-50')}>
          <div className="space-y-3 opacity-40">
            <div className="h-4 w-2/3 rounded bg-muted" />
            <div className="h-24 w-full rounded-xl bg-muted" />
            <div className="h-16 w-full rounded-lg bg-muted" />
          </div>
        </div>

        <div
          className={cn(
            'absolute inset-0 z-10 transition-opacity opacity-0 group-hover:opacity-100',
            settings.backdrop_blur ? 'bg-black/40 backdrop-blur-[2px]' : 'bg-black/40'
          )}
        />

        <div
          dir={direction}
          className={cn(
            'absolute inset-y-0 w-[85%] z-20 transition-transform duration-500 ease-in-out',
            fromLeft
              ? 'left-0 -translate-x-full group-hover:translate-x-0'
              : 'right-0 translate-x-full group-hover:translate-x-0',
            isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200',
            fromLeft ? 'border-r' : 'border-l'
          )}
        >
          <div className="h-full flex flex-col">
            <div className="p-4 border-b border-border/20 flex items-center justify-between">
              <h3 className={cn('text-sm font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
                {title}
              </h3>
              <X className="w-4 h-4 text-muted-foreground" />
            </div>

            {settings.enable_search && (
              <div className="p-3">
                <div
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-lg text-xs',
                    isDark ? 'bg-white/5 text-white/50' : 'bg-slate-100 text-slate-500'
                  )}
                >
                  <Search className="w-3.5 h-3.5" />
                  {isRtl ? 'بحث...' : 'Search...'}
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-2 agent-scrollbar">
              {sessions.length === 0 ? (
                <p
                  className={cn(
                    'text-[11px] text-center py-8',
                    isDark ? 'text-white/40' : 'text-slate-400'
                  )}
                >
                  {isRtl ? 'لا توجد محادثات بعد' : 'No conversations yet'}
                </p>
              ) : (
                sessions.slice(0, 6).map((session) => (
                  <div
                    key={session.id}
                    className={cn(
                      'p-2.5 rounded-xl border',
                      isDark ? 'border-white/5 bg-white/5' : 'border-slate-100 bg-slate-50'
                    )}
                  >
                    <div className="flex gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
                        <User className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 min-w-0 space-y-1">
                        <p
                          className={cn(
                            'text-[11px] font-medium truncate',
                            isDark ? 'text-white' : 'text-slate-900'
                          )}
                          dir={session.language === 'ar' ? 'rtl' : 'ltr'}
                        >
                          {session.first_message || 'Conversation'}
                        </p>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[9px] text-muted-foreground">
                            {formatWhen(session.created_at)}
                          </span>
                          {settings.show_badges && (
                            <ResolutionBadge
                              state={session.resolution_state}
                              className="text-[8px] px-1.5 py-0"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SettingToggle = ({ label, hint, checked, onChange }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="space-y-0.5 min-w-0">
      <Label className="text-sm">{label}</Label>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} />
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);

function maskUser(id) {
  if (!id || id === 'anonymous') return 'anonymous';
  if (id.length <= 8) return id;
  return `${id.slice(0, 6)}**${id.slice(-3)}`;
}

export default ChatHistoryPage;
